import React, { useContext, useEffect } from 'react';
import { EditCustomerContext, IEditCustomerContext } from '../../editCustomerState/editCustomerContext';
import { UserRoles } from 'types';
import { useRoleAccess } from 'hooks/useRoleAccess';
import { FormTabs } from 'components/EntityForm/FormTabs';
import { FormTab } from 'components/EntityForm/FormTab';
import WithAdditionalValidationState from 'components/WithAdditionalValidation/WithAdditionalValidationState';
import { LocalizationState } from './LocalizationForm/localizationState/LocalizationState';
import { StyleState } from './StyleForm/styleState/StyleState';
import { BpaState } from './BpaForm/bpaState/BpaState';
import { MainInfoForm } from './MainInfoForm';
import { LocalizationForm } from './LocalizationForm';
import { StyleForm } from './StyleForm';
import { BpaForm } from './BpaForm';

export const CustomerForms: React.FC = () => {
	const { changeVisibleForm, visibleForm, initialFormData } = useContext<IEditCustomerContext>(EditCustomerContext);
	const hasMainFormAccess = useRoleAccess([UserRoles.SUPER_USER]);
	const hasLocalizationFormAccess = useRoleAccess([
		UserRoles.SUPER_USER,
		UserRoles.CLIENT_MANAGER,
		UserRoles.GROUP_MANAGER,
		UserRoles.TECHNICAL,
	]);
	const hasStyleFormAccess = useRoleAccess([
		UserRoles.SUPER_USER,
		UserRoles.CLIENT_MANAGER,
		UserRoles.GROUP_MANAGER,
		UserRoles.TECHNICAL,
	]);
	const hasBpaFormAccess = useRoleAccess([UserRoles.SUPER_USER]);

	useEffect(() => {
		window.scrollTo(0, 0);
	}, [visibleForm]);

	useEffect(() => {
		if (hasMainFormAccess) {
			changeVisibleForm('mainForm');
		} else if (hasLocalizationFormAccess) {
			changeVisibleForm('localizationForm');
		} else if (hasStyleFormAccess) {
			changeVisibleForm('styleForm');
		} else if (hasBpaFormAccess) {
			changeVisibleForm('bpaForm');
		}
	}, [hasMainFormAccess, hasLocalizationFormAccess, hasStyleFormAccess, hasBpaFormAccess]);

	return (
		<div className="min-h-screen" data-test="Forms">
			<div className="pt-7 pr-4">
				<FormTabs>
					{hasMainFormAccess && (
						<FormTab
							translationKey="mainInfoBookmark"
							isActive={visibleForm === 'mainForm'}
							testId="mainInfoBookmark"
							onClick={() => changeVisibleForm('mainForm')}
						/>
					)}
					{hasLocalizationFormAccess && (
						<FormTab
							translationKey="localizationBookmark"
							isActive={visibleForm === 'localizationForm'}
							testId="localizationBookmark"
							onClick={() => changeVisibleForm('localizationForm')}
						/>
					)}
					{hasStyleFormAccess && (
						<FormTab
							translationKey="styleBookmark"
							isActive={visibleForm === 'styleForm'}
							testId="styleBookmark"
							onClick={() => changeVisibleForm('styleForm')}
						/>
					)}
					{hasBpaFormAccess && initialFormData?.isActiveBpa && (
						<FormTab
							translationKey="bpaBookmark"
							isActive={visibleForm === 'bpaForm'}
							testId="bpaBookmark"
							onClick={() => changeVisibleForm('bpaForm')}
						/>
					)}
				</FormTabs>
			</div>
			{hasMainFormAccess && (
				<WithAdditionalValidationState>
					<MainInfoForm isVisible={visibleForm === 'mainForm'} />
				</WithAdditionalValidationState>
			)}
			{hasLocalizationFormAccess && (
				<LocalizationState>
					<LocalizationForm isVisible={visibleForm === 'localizationForm'} />
				</LocalizationState>
			)}
			{hasStyleFormAccess && (
				<StyleState>
					<StyleForm isVisible={visibleForm === 'styleForm'} />
				</StyleState>
			)}
			{hasBpaFormAccess && (
				<BpaState>
					<BpaForm isVisible={visibleForm === 'bpaForm'} />
				</BpaState>
			)}
		</div>
	);
};
